import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { useConfirm } from "@/components/ConfirmDialog";
import { KebabMenu } from "@/components/KebabMenu";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { hasRole } from "@/lib/hasRole";
import { plural } from "@/lib/plural";
import { DynamicRuleModal } from "./DynamicRuleModal";
import type { TeamLayoutContext } from "./TeamLayout";
import { resourceTypeLabel, type AccessRule } from "./shared";
import { StaticRuleModal } from "./StaticRuleModal";
import { LoadError } from "@/components/LoadError";

type ModalState = { rule: AccessRule | null } | null;

export function TeamAccessRules() {
  const { accessToken, user } = useAuth();
  const api = useApi();
  const { orgId, teamId } = useParams();
  const { teamName } = useOutletContext<TeamLayoutContext>();
  const { toast, showToast } = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [rules, setRules] = useState<AccessRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [staticModal, setStaticModal] = useState<ModalState>(null);
  const [dynamicModal, setDynamicModal] = useState<ModalState>(null);

  const isAdmin = hasRole(user?.roles, "Admin");

  const fetchRules = useCallback(async () => {
    if (!accessToken || !orgId || !teamId) {
      return null;
    }

    return api.get<AccessRule[]>(
      `/organizations/${orgId}/teams/${teamId}/access-rules`,
    );
  }, [accessToken, api, orgId, teamId]);

  const applyRules = useCallback((data: AccessRule[] | null) => {
    if (data) {
      setRules(data);
      setError(null);
    } else {
      setError("Couldn't load access rules.");
    }
  }, []);

  const reloadRules = useCallback(async () => {
    applyRules(await fetchRules());
  }, [applyRules, fetchRules]);

  useEffect(() => {
    let cancelled = false;

    void fetchRules().then((data) => {
      if (!cancelled) {
        applyRules(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchRules, applyRules]);

  async function handleDelete(rule: AccessRule) {
    if (!accessToken || !orgId || !teamId) {
      return;
    }

    const confirmed = await confirm(
      `The team loses access to ${plural(rule.resourceCount, "resource")} immediately.`,
      { title: `Delete ${rule.name}?`, confirmLabel: "Delete" },
    );

    if (!confirmed) {
      return;
    }

    try {
      await api.send(
        `/organizations/${orgId}/teams/${teamId}/access-rules/${rule.id}`,
        "DELETE",
        undefined,
        "Couldn't delete rule.",
      );

      showToast(`${rule.name} deleted.`, "success");
      await reloadRules();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't delete rule.",
        "error",
      );
    }
  }

  const staticRules = (rules ?? []).filter((rule) => rule.kind === "Static");
  const dynamicRules = (rules ?? []).filter((rule) => rule.kind === "Dynamic");

  function retry() {
    setError(null);
    setRules(null);
    void reloadRules();
  }

  if (error && !rules) {
    return <LoadError what="access rules" onRetry={retry} />;
  }

  if (!rules) {
    return (
      <div className="animate-pulse space-y-6">
        {Array.from({ length: 2 }, (_, section) => (
          <div key={section}>
            <div className="mb-3 h-4 w-40 rounded bg-surface" />
            <div className="space-y-3 rounded-lg bg-surface/40 p-4">
              {Array.from({ length: 2 }, (_, row) => (
                <div key={row}>
                  <div className="h-3 w-48 rounded bg-surface" />
                  <div className="mt-1.5 h-2.5 w-32 rounded bg-surface" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Static rules
            </h2>
            <p className="text-xs text-foreground/70">
              Resources you select individually.
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="primary"
              onClick={() => setStaticModal({ rule: null })}
            >
              Add rule
            </Button>
          )}
        </div>
        {staticRules.length === 0 ? (
          <div className="rounded-lg border border-muted bg-surface/40 p-8 text-center">
            <p className="text-sm font-medium text-foreground">
              No static rules yet
            </p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-foreground/70">
              Grant access by selecting resources individually.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-muted rounded-lg bg-surface p-4">
            {staticRules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <div>
                  <div className="text-sm text-foreground">{rule.name}</div>
                  <div className="text-xs text-foreground/70">
                    {rule.resourceCount} resource
                    {rule.resourceCount === 1 ? "" : "s"}
                  </div>
                </div>
                {isAdmin && (
                  <KebabMenu
                    label={`More actions for ${rule.name}`}
                    items={[
                      {
                        label: "Edit",
                        onClick: () => setStaticModal({ rule }),
                      },
                      {
                        label: "Delete",
                        danger: true,
                        onClick: () => handleDelete(rule),
                      },
                    ]}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Dynamic rules
            </h2>
            <p className="text-xs text-foreground/70">
              Resources that match a tag, now and in future.
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="primary"
              onClick={() => setDynamicModal({ rule: null })}
            >
              Add rule
            </Button>
          )}
        </div>
        {dynamicRules.length === 0 ? (
          <div className="rounded-lg border border-muted bg-surface/40 p-8 text-center">
            <p className="text-sm font-medium text-foreground">
              No dynamic rules yet
            </p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-foreground/70">
              Grant access by tag so resources are selected automatically.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-muted rounded-lg bg-surface p-4">
            {dynamicRules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <div>
                  <div className="text-sm text-foreground">{rule.name}</div>
                  <div className="text-xs text-foreground/70">
                    {resourceTypeLabel(rule.resourceType)} where {rule.tagKey}=
                    {rule.tagValue} · {rule.resourceCount} resource
                    {rule.resourceCount === 1 ? "" : "s"}
                  </div>
                </div>
                {isAdmin && (
                  <KebabMenu
                    label={`More actions for ${rule.name}`}
                    items={[
                      {
                        label: "Edit",
                        onClick: () => setDynamicModal({ rule }),
                      },
                      {
                        label: "Delete",
                        danger: true,
                        onClick: () => handleDelete(rule),
                      },
                    ]}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {staticModal && (
        <StaticRuleModal
          rule={staticModal.rule}
          teamName={teamName}
          showToast={showToast}
          onClose={() => setStaticModal(null)}
          onSaved={() => {
            setStaticModal(null);
            void reloadRules();
          }}
        />
      )}

      {dynamicModal && (
        <DynamicRuleModal
          rule={dynamicModal.rule}
          teamName={teamName}
          showToast={showToast}
          onClose={() => setDynamicModal(null)}
          onSaved={() => {
            setDynamicModal(null);
            void reloadRules();
          }}
        />
      )}

      <Toast toast={toast} />
      {confirmDialog}
    </div>
  );
}
